import React, { useState } from 'react';
import { View, Image, ActivityIndicator, Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ThemedText } from '@/components/themed-text';
import { useToastStore } from '@/store/toastStore';
import { getApiHostHeaders, requireApiBaseUrl } from '@/lib/api-url';
import { appendCompressedImage } from '@/lib/image-upload';
import { useAuthStore } from '@/store/authStore';
import { Ionicons } from '@expo/vector-icons';
import { getFetchErrorMessage, getApiErrorMessage } from '@/utils/api-error';

interface ProofUploadModalProps {
  chargeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ProofUploadModal({
  chargeId,
  open,
  onOpenChange,
  onSuccess,
}: ProofUploadModalProps) {
  const { addToast } = useToastStore();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const pickImage = async () => {
    // Solicitar permissão
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para acessar galeria é necessária.', 'error');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setSelectedImage(result.assets[0].uri);
    }
  };

  const takePhoto = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para usar câmera é necessária.', 'error');
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setSelectedImage(result.assets[0].uri);
    }
  };

  const handleUpload = async () => {
    if (!selectedImage) {
      addToast('Selecione uma imagem primeiro.', 'error');
      return;
    }

    setIsUploading(true);

    try {
      console.log('[handleUpload] Starting upload for charge:', chargeId);
      console.log('[handleUpload] Selected image URI:', selectedImage);
      console.log('[handleUpload] Platform:', Platform.OS);

      // O picker só entrega imagens; reduz antes de enviar (foto de câmera passa de 4MB).
      const formData = new FormData();
      await appendCompressedImage(formData, 'file', selectedImage, 'proof');

      // Usar fetch nativo ao invés do axios para multipart/form-data
      // pois o axios tem problemas conhecidos com boundary no React Native
      const API_URL = requireApiBaseUrl().replace(/\/$/, '');
      const token = useAuthStore.getState().token;

      console.log(
        '[handleUpload] Sending request to:',
        `${API_URL}/charge/${chargeId}/upload-proof`,
      );

      const response = await fetch(`${API_URL}/charge/${chargeId}/upload-proof`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          ...getApiHostHeaders(),
          // NÃO DEFINIR Content-Type manualmente para que o browser/RN defina o boundary correto
        },
      });

      console.log('[handleUpload] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[handleUpload] Upload failed:', errorData);
        throw new Error(
          getFetchErrorMessage(response.status, errorData, 'Erro ao enviar comprovante.'),
        );
      }

      addToast('Comprovante enviado com sucesso!', 'success');
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error('Erro no upload:', error);
      addToast(getApiErrorMessage(error, 'Erro ao enviar comprovante.'), 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedImage(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !isUploading && onOpenChange(value)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <ThemedText className="font-bold text-xl">Enviar Comprovante</ThemedText>
          </DialogTitle>
          <DialogDescription>
            <ThemedText className="text-sm text-foreground/60">
              Anexe o comprovante de pagamento para informar o prestador.
            </ThemedText>
          </DialogDescription>
        </DialogHeader>

        <View className="mt-4 gap-4">
          {/* Imagem Selecionada */}
          {selectedImage ? (
            <View className="relative items-center gap-3">
              <Image
                source={{ uri: selectedImage }}
                accessibilityLabel="Comprovante selecionado"
                className="h-48 w-full rounded-lg"
                resizeMode="contain"
              />
              {isUploading && (
                <View className="absolute inset-0 items-center justify-center rounded-lg bg-black/40">
                  <ActivityIndicator size="large" color="white" />
                  <ThemedText className="mt-2 font-bold text-white">Enviando...</ThemedText>
                </View>
              )}
              <Button
                variant="ghost"
                size="sm"
                onPress={() => setSelectedImage(null)}
                disabled={isUploading}
              >
                <ThemedText className="text-foreground/60">Remover imagem</ThemedText>
              </Button>
            </View>
          ) : (
            <View className="gap-3">
              {/* Botões de seleção */}
              <Button variant="outline" onPress={pickImage}>
                <View className="flex-row items-center gap-2">
                  <Ionicons className="text-foreground" name="images-outline" size={20} />
                  <ThemedText>Escolher da galeria</ThemedText>
                </View>
              </Button>

              {Platform.OS !== 'web' && (
                <Button variant="outline" onPress={takePhoto}>
                  <View className="flex-row items-center gap-2">
                    <Ionicons className="text-foreground" name="camera-outline" size={20} />
                    <ThemedText>Tirar foto</ThemedText>
                  </View>
                </Button>
              )}
            </View>
          )}
        </View>

        <DialogFooter className="mt-6">
          <Button variant="outline" onPress={handleClose} disabled={isUploading}>
            <ThemedText>Cancelar</ThemedText>
          </Button>
          <Button onPress={handleUpload} disabled={!selectedImage || isUploading}>
            {isUploading ? (
              <View className="flex-row items-center gap-2">
                <ActivityIndicator size="small" color="white" />
                <ThemedText className="text-white">Enviando...</ThemedText>
              </View>
            ) : (
              <ThemedText className="text-white">Enviar comprovante</ThemedText>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
